import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDashboardActivity, getDashboardSummary } from '@/api/dashboard.api';
import { ApiError } from '@/api/errors';
import { STAT_CARD_WIDGETS } from '@/lib/dashboardWidgets';
import type { WidgetFetchStatus } from '@/lib/useWidgetRetryFocus';
import { StatCardWidget } from '@/pages/dashboard/StatCardWidget';
import { ActivityFeedWidget } from '@/pages/dashboard/ActivityFeedWidget';
import {
  DashboardFilterBar,
  type DashboardFilterBarFieldErrors,
} from '@/pages/dashboard/DashboardFilterBar';
import type {
  DashboardActivityItem,
  DashboardCardKey,
  DashboardStatCard,
  DashboardSummaryPeriod,
  DashboardSummaryQueryParams,
  DashboardSummaryResponse,
  ResolvedPeriod,
} from '@/types/shared';
import './AdminDashboardGrid.css';

/**
 * Admin's role-aware widget set (T85, `11-dashboard-widget-grid-ux.md`; period filter bar added
 * T97/T98/T100). Fixed DOM order (never reordered via CSS `order` — §1.1/§5): the filter bar, the
 * 5 stat cards, then the always-full-width activity feed. The 5 cards share ONE
 * `/dashboard/summary` fetch (T81 Decision D — a single cheap parallel-SQL group that
 * succeeds/fails as a unit), so they error/retry together; the activity feed is a fully
 * independent `/dashboard/activity` fetch that takes no period param and never refetches on a
 * period change (T100 §3) — one group failing never blanks the other.
 *
 * T97 opt-in semantics: the initial load and the "no pill selected" state send NO `period` param
 * at all (byte-for-byte the pre-T98 request) — only an explicit pill click or a Custom-range Apply
 * ever adds one.
 */
export function AdminDashboardGrid() {
  const { t } = useTranslation();

  const [cardsByKey, setCardsByKey] = useState<
    Partial<Record<DashboardCardKey, DashboardStatCard>>
  >({});
  /** Only set from a response that actually echoed one (T98 — omitted entirely on the default,
   * unfiltered request). `null` = the default view; both scoped cards render their unchanged
   * default titles in that case. */
  const [resolvedPeriod, setResolvedPeriod] = useState<ResolvedPeriod | null>(null);
  const [summaryStatus, setSummaryStatus] = useState<WidgetFetchStatus>('loading');

  /**
   * The `/dashboard/summary` query params actually in flight / last committed — a ref (not
   * state) so it never itself forces the fetch effect to re-run; only bumping
   * `summaryFetchToken` does. This lets the Custom-range Apply flow (below) run its OWN fetch
   * outside the generic effect — so a 400 surfaces as inline field errors on the filter bar
   * rather than flipping the whole 5-card group into its shared error state — while Retry and
   * every pill click still share this one fetch effect.
   */
  const summaryParamsRef = useRef<DashboardSummaryQueryParams | undefined>({ period: 'today' });
  const [summaryFetchToken, setSummaryFetchToken] = useState(0);
  /**
   * Monotonic id for the most recently *started* summary request, across BOTH the shared fetch
   * effect and the Custom-range Apply path. Each request captures the value it bumped this to and
   * only commits its response if it's still the latest — so a slow Apply can't clobber a newer
   * pill click (or vice versa) once the user has moved on. (`cancelled` alone doesn't cover it:
   * the two paths live in different closures and neither unmounts the other.)
   */
  const summaryReqSeq = useRef(0);

  // Filter-bar UI state — independent of the committed fetch params above (e.g. the user can
  // type into the custom-range inputs without triggering anything until Apply).
  const [selectedPeriod, setSelectedPeriod] = useState<DashboardSummaryPeriod | null>('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [customApplying, setCustomApplying] = useState(false);
  const [filterFieldErrors, setFilterFieldErrors] = useState<DashboardFilterBarFieldErrors>({});
  const [filterGeneralError, setFilterGeneralError] = useState<string | null>(null);

  const [activityItems, setActivityItems] = useState<DashboardActivityItem[]>([]);
  const [activityStatus, setActivityStatus] = useState<WidgetFetchStatus>('loading');
  const [activityReloadToken, setActivityReloadToken] = useState(0);

  function applySummaryResponse(response: DashboardSummaryResponse) {
    const map: Partial<Record<DashboardCardKey, DashboardStatCard>> = {};
    for (const card of response.cards) {
      map[card.key] = card;
    }
    setCardsByKey(map);
    setResolvedPeriod(response.resolvedPeriod ?? null);
  }

  // The one shared fetch path for the initial default load, every non-custom pill click, the
  // deselect-back-to-default click, and the group Retry button — reads `summaryParamsRef.current`
  // (not a reactive dependency) so bumping the token alone (Retry) re-sends the exact same
  // request, while `triggerSummaryFetch` (pill clicks) sets a new one first.
  useEffect(() => {
    const reqId = ++summaryReqSeq.current;
    setSummaryStatus('loading');

    getDashboardSummary(summaryParamsRef.current)
      .then((response) => {
        if (reqId !== summaryReqSeq.current) return;
        applySummaryResponse(response);
        setSummaryStatus('loaded');
      })
      .catch(() => {
        if (reqId !== summaryReqSeq.current) return;
        setSummaryStatus('error');
      });
  }, [summaryFetchToken]);

  useEffect(() => {
    let cancelled = false;
    setActivityStatus('loading');

    getDashboardActivity()
      .then((response) => {
        if (cancelled) return;
        setActivityItems(response.items);
        setActivityStatus('loaded');
      })
      .catch(() => {
        if (cancelled) return;
        setActivityStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [activityReloadToken]);

  function triggerSummaryFetch(params: DashboardSummaryQueryParams | undefined) {
    summaryParamsRef.current = params;
    setSummaryFetchToken((v) => v + 1);
  }

  function handlePillClick(period: DashboardSummaryPeriod) {
    setFilterFieldErrors({});
    setFilterGeneralError(null);

    if (selectedPeriod === period) {
      // T97 "provide a way back to the unfiltered default without reload" — re-clicking the
      // active pill deselects it and goes back to sending no period at all.
      setSelectedPeriod(null);
      triggerSummaryFetch(undefined);
      return;
    }

    setSelectedPeriod(period);
    if (period === 'custom') {
      // Custom only refetches on explicit Apply (below) — selecting the pill just reveals the
      // from/to inputs.
      return;
    }
    triggerSummaryFetch({ period });
  }

  async function handleApplyCustomRange() {
    const from = customFrom.trim();
    const to = customTo.trim();

    const requiredErrors: DashboardFilterBarFieldErrors = {};
    if (!from) requiredErrors.from = t('dashboard.filterBar.errors.fromRequired');
    if (!to) requiredErrors.to = t('dashboard.filterBar.errors.toRequired');
    if (Object.keys(requiredErrors).length > 0) {
      setFilterFieldErrors(requiredErrors);
      return;
    }

    const hadPriorData = Object.keys(cardsByKey).length > 0;
    setFilterFieldErrors({});
    setFilterGeneralError(null);
    setCustomApplying(true);
    setSummaryStatus('loading');

    const params: DashboardSummaryQueryParams = { period: 'custom', from, to };
    const reqId = ++summaryReqSeq.current;
    try {
      const response = await getDashboardSummary(params);
      if (reqId !== summaryReqSeq.current) return; // superseded by a newer pill/apply — drop it
      applySummaryResponse(response);
      summaryParamsRef.current = params;
      setSummaryStatus('loaded');
    } catch (err) {
      if (reqId !== summaryReqSeq.current) return; // superseded — don't surface a stale error
      const apiError = ApiError.fromUnknown(err);
      if (apiError.details.length > 0) {
        const fieldErrors: DashboardFilterBarFieldErrors = {};
        let general: string | null = null;
        for (const detail of apiError.details) {
          if (detail.field === 'from') fieldErrors.from = detail.message;
          else if (detail.field === 'to') fieldErrors.to = detail.message;
          else general = detail.message;
        }
        setFilterFieldErrors(fieldErrors);
        setFilterGeneralError(general);
      } else {
        setFilterGeneralError(
          apiError.code === 'NETWORK_ERROR'
            ? t('dashboard.filterBar.errors.network')
            : t('dashboard.filterBar.errors.generic'),
        );
      }
      // An Apply that fails never blanks a grid that already had data on screen — the previous
      // cards/period stay put and the error surfaces inline on the filter bar instead; only a
      // failure on a still-empty grid (nothing successfully loaded yet) falls back to the shared
      // widget-group error state so the user still has a way to recover (the group Retry button).
      setSummaryStatus(hadPriorData ? 'loaded' : 'error');
    } finally {
      setCustomApplying(false);
    }
  }

  return (
    <div className="fa-dashboard-grid">
      <DashboardFilterBar
        selectedPeriod={selectedPeriod}
        onPillClick={handlePillClick}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFromChange={(value) => {
          setCustomFrom(value);
          setFilterFieldErrors((prev) => ({ ...prev, from: undefined }));
        }}
        onCustomToChange={(value) => {
          setCustomTo(value);
          setFilterFieldErrors((prev) => ({ ...prev, to: undefined }));
        }}
        onApply={() => void handleApplyCustomRange()}
        isBusy={customApplying}
        fieldErrors={filterFieldErrors}
        generalError={filterGeneralError}
      />

      <div className="fa-dashboard-grid__stats">
        {STAT_CARD_WIDGETS.map((widget) => (
          <StatCardWidget
            key={widget.key}
            widget={widget}
            card={cardsByKey[widget.key] ?? null}
            status={summaryStatus}
            onRetry={() => setSummaryFetchToken((v) => v + 1)}
            resolvedPeriod={resolvedPeriod}
          />
        ))}
      </div>

      <ActivityFeedWidget
        items={activityItems}
        status={activityStatus}
        onRetry={() => setActivityReloadToken((v) => v + 1)}
      />
    </div>
  );
}
